import { Button, Form, notification, Modal, Result, Spin } from "antd";
import React, { useEffect, useRef, useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { addFormModal, getFormModalControls } from "../../../service";
import { newIdempotencyKey } from "../../../utils/idempotency";
import { apolloClient } from "../../../apolloClient";
import { GET_ITEMS } from "../../../graphql/items";
import { GET_TASKS } from "../../../graphql/tasks";
import { ModalForm } from "../../molecules";

import { SingleOrderContextProvider, useMode } from "../../organisms";
import {
  createFormInitialValue,
  createShema,
  joiResolver,
} from "../../organisms/single-order/SingleOrder.utils";
import { MainTemplateLoading } from "../../templates/main-template/MainTemplate.styled";

export const FormModal: React.FC<{ control: any }> = ({ control }) => {
  const { mode, global_actions } = useMode();
  const [open, setOpen] = useState(false);
  const queryClient = useQueryClient();

  const { data, error, isError, isFetching } = useQuery<any, Error>(
    [`/form-modal-${control.uuid}`, control.uuid],
    () => getFormModalControls(control.value.fetch_url),
    {
      enabled: open,
    }
  );
  const schema = data && createShema(data.model);
  const methods = useForm({
    resolver: schema && joiResolver(schema),
  });

  const values = methods.watch();

  const { mutateAsync, isLoading } = useMutation(addFormModal, {
    onSuccess: async () => {
      try {
        await queryClient.refetchQueries("/orders");
        await queryClient.refetchQueries("/tasks");
        await apolloClient.refetchQueries({
          include: [GET_ITEMS, GET_TASKS],
        });
        notification.success({
          message: "Zapisano zmiany",
          style: {
            background: "#52c41a30",
          },
        });
        methods.reset();
        setOpen(false);
      } catch (error) {
        notification.error({
          message: "Pobierania nowych danych",
          style: {
            background: "#ff4d4f30",
          },
        });
        console.error("Wystąpił błąd podczas odświeżania danych:", error);
        setOpen(false);
      }
    },

    onError: (error: any, variables: any) => {
      const isPaymentConflict =
        error?.response?.status === 409 &&
        String(variables?.url ?? "").includes("/payments");
      if (isPaymentConflict) {
        notification.warning({
          message:
            "Wpłata o tym samym kluczu, ale innych danych, już istnieje. Lista została odświeżona.",
          style: {
            background: "#faad1430",
          },
        });
        return;
      }
      notification.error({
        message: "Błąd zapisu danych",
        style: {
          background: "#ff4d4f30",
        },
      });
    },
  });

  const isSubmittingRef = useRef(false);
  const idempotencyKeyRef = useRef<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const openModal = () => {
    if (!idempotencyKeyRef.current) {
      idempotencyKeyRef.current = newIdempotencyKey();
    }
    setOpen(true);
  };

  const submit = () => {
    if (isSubmittingRef.current) return;
    methods.handleSubmit(async (data) => {
      if (isSubmittingRef.current) return;
      isSubmittingRef.current = true;
      setIsSubmitting(true);
      if (!idempotencyKeyRef.current) {
        idempotencyKeyRef.current = newIdempotencyKey();
      }
      try {
        await mutateAsync({
          url: control.value.save_url,
          body: data,
          idempotencyKey: idempotencyKeyRef.current,
        });
        idempotencyKeyRef.current = null;
      } catch (error: any) {
        const isPaymentConflict =
          error?.response?.status === 409 &&
          String(control.value.save_url ?? "").includes("/payments");
        if (isPaymentConflict) {
          // Payment with this key already exists with different data:
          // do not create another one. Refresh list so user can edit it.
          idempotencyKeyRef.current = null;
          methods.reset();
          setOpen(false);
          try {
            await queryClient.refetchQueries("/orders");
            await apolloClient.refetchQueries({
              include: [GET_ITEMS, GET_TASKS],
            });
          } catch (refetchError) {
            console.error(refetchError);
          }
        } else {
          // Keep modal, form data and idempotency key: retry reuses same key,
          // backend returns existing payment instead of inserting duplicate.
          console.error(error);
        }
      } finally {
        isSubmittingRef.current = false;
        setIsSubmitting(false);
      }
    }, console.error)();
  };

  const canel = () => {
    idempotencyKeyRef.current = null;
    methods.reset();
    setOpen(false);
  };

  useEffect(() => {
    if (open && data) {
      methods.reset(createFormInitialValue(data.model));
      return;
    }
  }, [open, data]);

  return (
    <>
      <Form.Item
        // tooltip={
        //   !control.writeable && mode === "edit"
        //     ? "Brak uprawnień do edycji"
        //     : control.description
        // }
        style={{ margin: 0, marginTop: global_actions ? "" : "30px" }}
      >
        <Button
          type="primary"
          disabled={mode === "edit" || !control.writeable}
          onClick={openModal}
        >
          {control.field_name}
        </Button>
      </Form.Item>
      <Modal
        maskClosable={false}
        confirmLoading={isLoading}
        title="Dodaj nowy rekord"
        centered
        open={open}
        okButtonProps={{
          disabled: isError || isFetching || isLoading || isSubmitting,
          loading: isFetching || isLoading || isSubmitting,
        }}
        onOk={() => {
          submit();
        }}
        onCancel={canel}
        width={1000}
        destroyOnClose
      >
        {isFetching && (
          <MainTemplateLoading>
            <Spin size="large" />
          </MainTemplateLoading>
        )}

        {data && (
          <SingleOrderContextProvider disableReadMode={true}>
            <FormProvider {...methods}>
              <ModalForm controls={data.model} hardcodedControls={[]} />
            </FormProvider>
          </SingleOrderContextProvider>
        )}

        {isError && (
          <Result
            status="403"
            title="BŁĄD"
            subTitle="Przepraszamy, wystąpił błąd. Skontaktuj się z administratorem"
          />
        )}
      </Modal>
    </>
  );
};
